import { Typography } from '@weareopenr/component-library';
import styles from './PriceRuleTooltip.module.scss';
import * as Popover from '@radix-ui/react-popover';
import { CalculationInputType, CalculationStep, Rule } from '@weareopenr/common/build/src/utilities/pricing';
import { AdjustmentType, RoundingType, RoundingMethod } from '@weareopenr/types';
import { forwardRef, ReactElement, RefObject } from 'react';

const formatRuleAdjustment = (rule: Rule) => {
  const formatMap = {
    [AdjustmentType.FIXED]: `£${rule.adjustment.value.toFixed(2)}`,
    [AdjustmentType.PERCENTAGE]: `${rule.adjustment.value}%`,
  };
  return formatMap[rule.adjustment.type];
};

const roundingTypeMap = {
  [RoundingType.NEAREST]: 'Round to nearest',
  [RoundingType.UP]: 'Round up to',
  [RoundingType.DOWN]: 'Round down to',
};
const roundingMethodMap = {
  [RoundingMethod.INTERVAL]: 'interval of',
  [RoundingMethod.SPECIFIC]: 'specific value of',
};

const formatRuleRounding = (rule: Rule) => {
  return `${roundingTypeMap[rule.rounding.type]} ${roundingMethodMap[rule.rounding.method]} ${rule.rounding.value}`;
};

const inputTypeMap = {
  [CalculationInputType.BASE]: 'Base Price',
  [CalculationInputType.ADJUSTED]: 'Adjusted Price',
  [CalculationInputType.PRICE_BAND]: 'Band Price',
  [CalculationInputType.CHANNEL]: 'Channel Price',
};

export const PriceRuleToolTip = forwardRef<
  HTMLDivElement,
  {
    calculationSteps: CalculationStep[];
    tableContainerRef: RefObject<HTMLElement | null>;
  }
>(({ calculationSteps, tableContainerRef }, ref): ReactElement => {
  return (
    <Popover.Content
      ref={ref}
      className={styles['popover-content']}
      side="top"
      hideWhenDetached
      collisionBoundary={tableContainerRef.current}
      // Height of table header
      collisionPadding={{ top: 40 }}
    >
      <Popover.Arrow />
      {calculationSteps.map((step) => {
        return (
          <div key={step.stepNumber}>
            {calculationSteps.length > 1 && (
              <Typography colour="secondary" size="xsmall">
                Step {step.stepNumber}:
              </Typography>
            )}
            <Typography colour="secondary" size="xsmall">
              {step.rule.price_band?.id && 'Price Band'}
              {step.rule.external_system?.id && 'Channel'}: £{step.input} ({inputTypeMap[step.inputType]})
              {step.rule.adjustment.type !== AdjustmentType.NONE && ` + ${formatRuleAdjustment(step.rule)}`}
            </Typography>
            {step.rule.rounding.type !== RoundingType.NONE && (
              <Typography colour="secondary" size="xsmall">
                {formatRuleRounding(step.rule)}
              </Typography>
            )}
            <Typography colour="secondary" size="xsmall" weight="bold">
              {calculationSteps.length === step.stepNumber ? 'Final' : 'Adjusted'} price: £{step.output.toFixed(2)}
            </Typography>
          </div>
        );
      })}
    </Popover.Content>
  );
});

PriceRuleToolTip.displayName = 'PriceRuleToolTip';
